// context/AuthContext.tsx
import React, { useCallback,createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authService,  LoginResponse } from '../services/auth.service';
import { User } from '../types/user';
import { setCurrentDevise } from '../utils/currency';
import { organisationService } from '../services/organisationService';
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithTokens: (token: string, refresh?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
  permissionsLoaded: boolean; // Nouveau état pour suivre le chargement des permissions
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasModule: (module: string) => boolean;
  clearError: () => void;
  userPermissions: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false); // État crucial
  const [error, setError] = useState<string | null>(null);

  // Fonction pour charger les permissions utilisateur
  // Dans votre AuthContext, modifiez la fonction loadUserPermissions
// context/AuthContext.tsx - CORRECTION
// context/AuthContext.tsx - CORRECTION COMPLÈTE
const loadUserPermissions = useCallback((userData: User) => {
  // Les droits viennent UNIQUEMENT du profil backend.
  // Le rôle n'intervient PAS dans le calcul des permissions — seul le super_admin
  // a un bypass (géré dans hasPermission/hasModule, pas ici).
  // Si le profil n'a aucun droit, l'utilisateur ne voit que Mon Espace (permission 'authenticated').
  const permissions: string[] =
    userData.droits && Array.isArray(userData.droits) && userData.droits.length > 0
      ? [...userData.droits]
      : [];

  // Mise à jour synchrone des états
  setUserPermissions(permissions);
  setPermissionsLoaded(true);
}, []);
  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    // Liste des chemins publics qui ne doivent PAS déclencher de check d'auth
    // (évite les boucles de redirection si le token est expiré).
    //
    // IMPORTANT : tenir cette liste synchronisée avec :
    //   - PUBLIC_PATHS de AppRoutes.tsx (routing)
    //   - PUBLIC_PATHS de utils/authRedirect.ts (interceptor axios)
    //
    // Une route absente ici provoque un bug en prod : la SPA appelle
    // /auth/profile au boot, reçoit 401, l'interceptor déclenche un
    // logout silencieux puis une redirection vers /login alors que
    // l'utilisateur consulte une page publique.
    const PUBLIC_PATH_PREFIXES = [
      '/landing', '/souscrire', '/postuler', '/devis-validation',
      '/bien-public', '/contre-proposition', '/contrat-validation',
      '/espace-public', '/portail', '/api-zone',
      '/mot-de-passe-oublie', '/reinitialiser-mot-de-passe',
      '/sso/callback',
      '/demo-vie-scolaire', '/espace-parent',
      '/paiement/retour', '/cinetpay/retour', '/cotation',
      // Portail Patronat / Association — accès agents + adhérents via slug
      '/p/agents', '/p/adherents',
      // Socle « Vitrine Patronat » — pages publiques servies à la racine.
      // Tout le socle « Vitrine Patronat » vit sous /public/.
      '/public/',
    ];
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicPath = PUBLIC_PATH_PREFIXES.some(p => path.includes(p));

    const checkAuthStatus = async () => {
      try {
        if (authService.isAuthenticated()) {
          // Si on est sur une page publique ET que le token est peut-être expiré,
          // on évite l'appel à getProfile() pour ne pas déclencher de 401 +
          // redirect via l'intercepteur. L'auth sera revalidée à la prochaine
          // tentative d'accès à une page protégée.
          if (isPublicPath) {
            // On charge depuis le cache localStorage si dispo, sans appel API
            const cachedUser = authService.getUser();
            if (cachedUser) {
              setUser(cachedUser);
              loadUserPermissions(cachedUser);
            } else {
              setPermissionsLoaded(true);
            }
          } else {
            const userData = await authService.getProfile();
            setCurrentDevise(userData?.entiteDevise || 'GNF');
            setUser(userData);
            loadUserPermissions(userData);
            // Synchronise le cache localStorage organisation RH avec le backend
            organisationService.init().catch(() => {});
          }
        } else {
          setPermissionsLoaded(true);
        }
      } catch (err: any) {
        console.error('💥 Erreur lors de la vérification de l\'authentification:', err);
        // Serveur injoignable (réseau coupé, zone blanche sur le terrain) : ce
        // n'est pas un refus d'authentification. Déconnecter effacerait le
        // jeton, et l'agent ne pourrait plus se reconnecter avant le retour du
        // réseau — ni envoyer ses actions en attente. On reprend le profil
        // gardé localement ; le serveur revérifie le jeton à chaque appel.
        const serveurInjoignable = !err?.response;
        const cachedUser = serveurInjoignable ? authService.getUser() : null;
        if (cachedUser) {
          setUser(cachedUser);
          loadUserPermissions(cachedUser);
        } else if (!isPublicPath) {
          // Sur page publique : ne PAS forcer logout (évite la suppression du token
          // alors que l'utilisateur n'a même pas demandé à se connecter)
          authService.logout();
        }
        setPermissionsLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    setLoading(true);
    setError(null);
    setPermissionsLoaded(false); // Réinitialiser pendant la connexion

    try {
      const response = await authService.login(email, password, rememberMe);
      // Suppression du log de la réponse (contient les tokens)
      void response;

      const userData = authService.getUser();
      if (userData) {
        setCurrentDevise(userData?.entiteDevise || 'GNF');
        setUser(userData);
        loadUserPermissions(userData);
        // Synchronise le cache localStorage de l'organisation RH avec le backend
        // (fire-and-forget — n'attend pas la fin et ignore les erreurs réseau)
        organisationService.init().catch(() => {});
      } else {
        throw new Error('Impossible de récupérer les informations utilisateur');
      }
    } catch (err: any) {
      console.error('💥 Erreur de connexion:', err);
      setError(err.message || 'Erreur de connexion');
      setPermissionsLoaded(true); // En cas d'erreur, marquer comme chargé
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Connexion SSO (SAML) : le backend a déjà émis les tokens et redirige le
   * navigateur vers /sso/callback?token=…&refresh=…. On persiste les tokens
   * puis on hydrate l'utilisateur — même flux que `login` classique.
   */
  const loginWithTokens = async (token: string, refresh?: string): Promise<void> => {
    setLoading(true);
    setError(null);
    setPermissionsLoaded(false);
    try {
      const userData = await authService.loginWithTokens(token, refresh);
      if (userData) {
        setCurrentDevise(userData?.entiteDevise || 'GNF');
        setUser(userData);
        loadUserPermissions(userData);
        organisationService.init().catch(() => {});
      } else {
        throw new Error('Impossible de récupérer les informations utilisateur');
      }
    } catch (err: any) {
      console.error('💥 Erreur de connexion SSO:', err);
      setError(err.message || 'Erreur de connexion SSO');
      setPermissionsLoaded(true);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /** Recharge le profil utilisateur depuis le backend et met à jour le contexte */
  const refreshUser = async (): Promise<void> => {
    try {
      const userData = await authService.getProfile();
      authService.setUser(userData);
      setUser(userData);
      loadUserPermissions(userData);
    } catch (err) {
      console.error('Erreur lors du rafraîchissement du profil:', err);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setUserPermissions([]);
    setPermissionsLoaded(true); // Garder true pour éviter le spinner infini dans AppRoutes
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

const hasPermission = useCallback((permission: string): boolean => {
  try {
    // Vérifications de base
    if (!permissionsLoaded) {
      return false;
    }

    if (!user) {
      return false;
    }

    // Permission spéciale "authenticated" — tout utilisateur connecté
    if (permission === 'authenticated') {
      return true;
    }

    // Seul le super_admin a un bypass total — les admins d'entité (role='admin')
    // doivent respecter leurs droits de profil comme tout autre utilisateur.
    if (user.role === 'super_admin') {
      return true;
    }

    // Vérification standard des permissions
    return Array.isArray(userPermissions) && userPermissions.includes(permission);

  } catch (error) {
    console.error('💥 Erreur dans hasPermission:', error);
    return false;
  }
}, [permissionsLoaded, userPermissions, user]);

const hasModule = useCallback((module: string): boolean => {
  if (!user) return false;
  // Module spécial "authenticated" — tout utilisateur connecté
  if (module === 'authenticated') return true;
  // Seul le super_admin SAGeCOM (plateforme) a accès à tous les modules.
  // Les rôles 'admin' sont des admins d'entité — ils sont restreints aux modules
  // souscrits par leur entité (modulesActifs renvoyé par /auth/profile).
  if (user.role === 'super_admin') return true;
  // Wildcard explicite
  if (user.modulesActifs?.includes('*')) return true;
  // Si modulesActifs n'est pas peuplé ou vide → aucun module accessible (sécurité).
  // Cas légitime : entité sans pack souscrit ⇒ ne doit RIEN voir.
  if (!user.modulesActifs || user.modulesActifs.length === 0) return false;
  return user.modulesActifs.includes(module);
}, [user]);

  const value: AuthContextType = {
    user,
    login,
    loginWithTokens,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    loading,
    permissionsLoaded, // Exposer cet état
    error,
    hasPermission,
    hasModule,
    clearError,
    userPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};